import React from 'react'

import invariant from 'invariant'

import cn from '../../../../commons/src/libs/class-names'
import {mapChildrenWithFragments} from '../../../../commons/src/services/react-children'
import {getCellLayout} from './utils'

import ContentLoader from '../content-loader'

export interface Props {
  classNames?: string[]
  header: React.ReactElement
  children: React.ReactNode
  footer?: React.ReactNode
  isLoading?: boolean
  empty?: React.ReactNode
  border?: boolean
}

const Table = ({
  classNames,
  header,
  children,
  footer,
  empty = 'No entries',
  isLoading = false,
  border = false,
}: Props) => {
  invariant(
    mapChildrenWithFragments(header.props.children, child => React.isValidElement(child)).reduce(
      (acc, childBool) => acc && childBool,
      true
    ),
    'Header row children should be valid React elements'
  )

  const {aligns, computedWidths} = getCellLayout(header.props.children)
  const isEmpty = React.Children.toArray(children).length === 0

  return (
    <div className={cn('Table', {border}, classNames)}>
      <div className="Table__wrapper">
        <div className="Table__header">
          {React.cloneElement(header, {
            computedWidths,
            aligns,
          })}
        </div>
        <div className="Table__body">
          {isLoading && (
            <div className="Table__loader">
              <ContentLoader loading={isLoading} description="Loading..." />
            </div>
          )}
          {!isLoading && isEmpty && <div className="Table__empty">{empty}</div>}
          {!isLoading &&
            !isEmpty &&
            mapChildrenWithFragments(children, (child, i) =>
              React.cloneElement(child, {
                computedWidths,
                aligns,
                key: i,
              })
            )}
        </div>
        {footer && <div className="Table__footer">{footer}</div>}
      </div>
    </div>
  )
}

export default Table
